import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiCheck, FiEye, FiEyeOff, FiLock } from 'react-icons/fi';
import * as yup from 'yup';
import firebaseAuth from '~/config/firebase/auth';
import { useActivityLogs } from '~/hooks/useActivityLogs';
import { useAuth } from '~/hooks/useAuth';
import { resumirDispositivo } from '~/core/tracking/reservationEvents';

/**
 * Cambio de contraseña por parte del propio usuario.
 *
 * Firebase exige haber iniciado sesión hace poco para cambiar la contraseña,
 * así que primero se vuelve a autenticar con la actual y después se cambia. Ese
 * paso es además la comprobación de que quien está al teclado es el dueño de la
 * cuenta, y no alguien que encontró la sesión abierta.
 *
 * No cierra la sesión: al reautenticar, `authTime` se actualiza, así que el
 * vigilante de sesiones (`useSessionGuard`) no la da por revocada.
 */

const MINIMO = 6;

const esquema = yup.object({
  actual: yup.string().required('Escribe tu contraseña actual'),
  nueva: yup
    .string()
    .min(MINIMO, `Debe tener al menos ${MINIMO} caracteres`)
    .required('Escribe la nueva contraseña'),
  confirmar: yup
    .string()
    .oneOf([yup.ref('nueva')], 'Las contraseñas no coinciden')
    .required('Repite la nueva contraseña'),
});

type Campos = { actual: string; nueva: string; confirmar: string };

/** Mensajes claros en vez de los códigos de Firebase. */
const mensajeDeError = (codigo: string): string => {
  switch (codigo) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'La contraseña actual no es correcta.';
    case 'auth/weak-password':
      return `La nueva contraseña es demasiado débil: usa al menos ${MINIMO} caracteres.`;
    case 'auth/too-many-requests':
      return 'Demasiados intentos seguidos. Espera unos minutos y vuelve a probar.';
    case 'auth/network-request-failed':
      return 'No hay conexión. Revisa tu internet e inténtalo de nuevo.';
    case 'auth/requires-recent-login':
      return 'Por seguridad, cierra sesión, vuelve a entrar e inténtalo otra vez.';
    case 'auth/user-mismatch':
      return 'Esa contraseña no corresponde a esta cuenta.';
    default:
      return 'No se pudo cambiar la contraseña. Inténtalo de nuevo.';
  }
};

/** Fuerza orientativa, solo para guiar: lo que decide es el mínimo de caracteres. */
const fuerzaDe = (valor: string) => {
  if (!valor) return { puntos: 0, texto: '', color: 'gray' };
  let puntos = 0;
  if (valor.length >= MINIMO) puntos++;
  if (valor.length >= 10) puntos++;
  if (/[A-Z]/.test(valor) && /[a-z]/.test(valor)) puntos++;
  if (/\d/.test(valor)) puntos++;
  if (/[^A-Za-z0-9]/.test(valor)) puntos++;
  if (puntos <= 2) return { puntos, texto: 'Débil', color: 'red' };
  if (puntos === 3) return { puntos, texto: 'Aceptable', color: 'orange' };
  if (puntos === 4) return { puntos, texto: 'Buena', color: 'green' };
  return { puntos, texto: 'Muy buena', color: 'green' };
};

type CampoProps = {
  etiqueta: string;
  error?: string;
  ayuda?: string;
  autoComplete: string;
  registro: ReturnType<ReturnType<typeof useForm<Campos>>['register']>;
};

const CampoContrasena = ({ etiqueta, error, ayuda, autoComplete, registro }: CampoProps) => {
  const [visible, setVisible] = useState(false);
  return (
    <FormControl isInvalid={Boolean(error)}>
      <FormLabel fontSize='sm' mb={1}>
        {etiqueta}
      </FormLabel>
      <InputGroup size='md'>
        <Input
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          borderRadius='md'
          {...registro}
        />
        <InputRightElement>
          <IconButton
            size='sm'
            variant='ghost'
            tabIndex={-1}
            aria-label={visible ? 'Ocultar la contraseña' : 'Ver la contraseña'}
            icon={<Icon as={visible ? FiEyeOff : FiEye} />}
            onClick={() => setVisible((v) => !v)}
          />
        </InputRightElement>
      </InputGroup>
      {error ? (
        <FormErrorMessage fontSize='xs'>{error}</FormErrorMessage>
      ) : (
        ayuda && (
          <Text fontSize='xs' color='gray.500' mt={1}>
            {ayuda}
          </Text>
        )
      )}
    </FormControl>
  );
};

type Props = { isOpen: boolean; onClose: () => void };

const CambiarContrasena = ({ isOpen, onClose }: Props) => {
  const { logActivity } = useActivityLogs();
  const { user } = useAuth();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Campos>({
    mode: 'onBlur',
    resolver: yupResolver(esquema),
    defaultValues: { actual: '', nueva: '', confirmar: '' },
  });

  const nueva = watch('nueva') ?? '';
  const fuerza = useMemo(() => fuerzaDe(nueva), [nueva]);

  // Cada vez que se abre, empieza de cero: nada de contraseñas colgando.
  useEffect(() => {
    if (isOpen) {
      reset({ actual: '', nueva: '', confirmar: '' });
      setError(null);
      setListo(false);
    }
  }, [isOpen, reset]);

  const cerrar = () => {
    reset({ actual: '', nueva: '', confirmar: '' });
    setError(null);
    setListo(false);
    onClose();
  };

  const guardar = async ({ actual, nueva: nuevaClave }: Campos) => {
    setError(null);
    const cuenta = firebaseAuth.currentUser;
    if (!cuenta?.email) {
      setError('La sesión caducó. Cierra sesión, vuelve a entrar e inténtalo otra vez.');
      return;
    }
    if (actual === nuevaClave) {
      setError('La nueva contraseña tiene que ser distinta de la actual.');
      return;
    }
    setGuardando(true);
    try {
      // Comprueba que es el dueño de la cuenta antes de dejarle cambiarla.
      await reauthenticateWithCredential(
        cuenta,
        EmailAuthProvider.credential(cuenta.email, actual)
      );
      await updatePassword(cuenta, nuevaClave);

      // Queda constancia en el log interno: quién, cuándo y desde qué equipo.
      // Nunca se guarda ninguna contraseña, ni la vieja ni la nueva.
      logActivity({
        activityType: 'password_changed',
        entityType: 'user',
        entityId: cuenta.uid,
        companyId: user?.company?.id,
        details: {
          correo: cuenta.email,
          dispositivo: resumirDispositivo(navigator.userAgent),
          momento: new Date().toISOString(),
        },
      });

      setListo(true);
      reset({ actual: '', nueva: '', confirmar: '' });
    } catch (e) {
      const codigo = (e as { code?: string }).code ?? '';
      setError(mensajeDeError(codigo));

      // Un fallo al escribir la contraseña actual puede ser un despiste o
      // alguien probando suerte en una sesión ajena. Conviene que se vea.
      logActivity({
        activityType: 'password_change_failed',
        entityType: 'user',
        entityId: cuenta.uid,
        companyId: user?.company?.id,
        details: {
          correo: cuenta.email,
          motivo: codigo || 'desconocido',
          dispositivo: resumirDispositivo(navigator.userAgent),
          momento: new Date().toISOString(),
        },
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={cerrar} isCentered size='md' closeOnOverlayClick={!guardando}>
      <ModalOverlay backdropFilter='blur(2px)' />
      <ModalContent as='form' onSubmit={handleSubmit(guardar)}>
        <ModalHeader pb={2}>
          <HStack spacing={2}>
            <Icon as={FiLock} color='blue.500' />
            <Text fontSize='md'>Cambiar contraseña</Text>
          </HStack>
          <Text fontSize='sm' fontWeight='normal' color='gray.500' mt={1}>
            {firebaseAuth.currentUser?.email ?? ''}
          </Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={guardando} />

        <ModalBody pb={2}>
          {listo ? (
            <VStack py={6} spacing={3}>
              <Box
                bg='green.50'
                borderRadius='full'
                w='56px'
                h='56px'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <Icon as={FiCheck} color='green.500' boxSize={7} />
              </Box>
              <Text fontWeight='semibold'>Contraseña cambiada</Text>
              <Text fontSize='sm' color='gray.600' textAlign='center'>
                Úsala la próxima vez que entres. No hace falta que cierres sesión ahora.
              </Text>
            </VStack>
          ) : (
            <VStack align='stretch' spacing={4}>
              {error && (
                <Alert status='error' borderRadius='md' py={2}>
                  <AlertIcon boxSize={4} />
                  <Text fontSize='sm'>{error}</Text>
                </Alert>
              )}

              <CampoContrasena
                etiqueta='Contraseña actual'
                autoComplete='current-password'
                error={errors.actual?.message}
                registro={register('actual')}
              />

              <Box>
                <CampoContrasena
                  etiqueta='Nueva contraseña'
                  autoComplete='new-password'
                  ayuda={`Al menos ${MINIMO} caracteres.`}
                  error={errors.nueva?.message}
                  registro={register('nueva')}
                />
                {nueva.length > 0 && (
                  <HStack mt={2} spacing={3}>
                    <Progress
                      value={(fuerza.puntos / 5) * 100}
                      size='xs'
                      colorScheme={fuerza.color}
                      borderRadius='full'
                      flex={1}
                    />
                    <Text fontSize='xs' color={`${fuerza.color}.600`} minW='68px'>
                      {fuerza.texto}
                    </Text>
                  </HStack>
                )}
              </Box>

              <CampoContrasena
                etiqueta='Repite la nueva contraseña'
                autoComplete='new-password'
                error={errors.confirmar?.message}
                registro={register('confirmar')}
              />
            </VStack>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          {listo ? (
            <Button colorScheme='blue' onClick={cerrar}>
              Listo
            </Button>
          ) : (
            <>
              <Button variant='ghost' onClick={cerrar} isDisabled={guardando}>
                Cancelar
              </Button>
              <Button
                type='submit'
                colorScheme='blue'
                isLoading={guardando}
                loadingText='Guardando…'
              >
                Cambiar contraseña
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CambiarContrasena;
